import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { loadStripe } from '@stripe/stripe-js';
import LoadingOverlay from './components/LoadingOverlay';

// Define the props that this component will receive from App.tsx
interface BloggenPageProps {
  getAccessTokenSilently: () => Promise<string>;
}

const BloggenPage: React.FC<BloggenPageProps> = ({ getAccessTokenSilently }) => {
  // All form-related state and logic is now encapsulated here
  const [preferences, setPreferences] = useState('');
  const [topicInput, setTopicInput] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const stripePromise = loadStripe(stripePublicKey ?? '');

  const loadingMessages = [
    "Brewing your blog magic ☕",
    "Crafting insights just for you ✍️",
    "Great content loading... ⏳",
    "Ideas coming to life 💡",
    "Sharpening the narrative ✨",
    "Polishing your thoughts 🧠",
    "Typing brilliance behind the scenes ⌨️",
    "Your words are taking shape 🛠️",
    "Blog wizardry in progress 🪄",
    "Almost ready to inspire 🚀"
  ];

  const handleAddTopic = (): void => {
    const trimmedTopic = topicInput.trim();
    if (trimmedTopic.length > 1000) {
      alert('Topic is too long.');
      return;
    }
    const sanitizedTopic = DOMPurify.sanitize(trimmedTopic);
    if (!sanitizedTopic) {
      alert('Topic cannot be empty.');
      return;
    }
    if (topics.includes(sanitizedTopic)) {
      alert('Topic already added.');
      return;
    }
    setTopics(prev => [...prev, sanitizedTopic]);
    setTopicInput('');
  };

  const handleRemoveTopic = (indexToRemove: number): void => {
    setTopics(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowOverlay(true);
    setSuccess(false);

    const sanitizedPreferences = DOMPurify.sanitize(preferences).trim();
    const sanitizedTopicsArray = topics
      .map(topic => DOMPurify.sanitize(topic.trim()))
      .filter(topic => topic.length > 0);

    if (!sanitizedPreferences || sanitizedPreferences.length < 5) {
      alert('Please enter preferences (min 5 characters).');
      setShowOverlay(false);
      return;
    }

    try {
      const payload = {
        preferences: sanitizedPreferences,
        topics: sanitizedTopicsArray
      };
      
      const token = await getAccessTokenSilently();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        alert('Blogs generated successfully!');
        setSuccess(true);
        const zipFile = result.zip_filename;
        localStorage.setItem('zipFilename', zipFile);
        localStorage.setItem('checkoutStatus', 'pending');

        setTimeout(async () => {
          const stripe = await stripePromise;
          const checkoutSessionRes = await fetch(`${process.env.REACT_APP_API_URL}/api/create-checkout-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zip_filename: zipFile })
          });

          if (!checkoutSessionRes.ok) {
            const errorDetails = await checkoutSessionRes.json();
            alert('Checkout session failed: ' + errorDetails.error);
            setShowOverlay(false);
            return;
          }

          const session = await checkoutSessionRes.json();
          await stripe?.redirectToCheckout({ sessionId: session.id });
        }, 1500);

      } else {
        alert(`Server error: ${result.error}`);
        setShowOverlay(false);
      }
    } catch (error: any) {
      toast.error('Something went wrong!');
      alert('Network error: Could not reach the server.');
      setShowOverlay(false);
    }
  };

  return (
    <div className="bloggen-container">
      <section className="intro-section">
        <h1>Welcome to a Faster, Easier Way to Scale Your Content</h1>
        <p>
          Just tell us your preferences and upload your list of blog topics—we handle the rest. We can generate blog articles in bulk, ready for you to download and use in your projects. Less hassle, less delays—thank us for more productive days!
        </p>
      </section>

      <section className="how-it-works-section">
        <h2>How It Works</h2>
        <ol>
          <li><strong>Describe Your Preferences:</strong> Describe your preferences (e.g., tone, target audience, keywords, length, or any other content guidelines).</li>
          <li><strong>Give Us Your Blog Topics:</strong> Give us your list of blog titles or blog topics.</li>
          <li><strong>We Generate the Content:</strong> We create a blog article for each title or topic in the list.</li>
          <li><strong>Download Your Articles:</strong> Once ready, you’ll receive a link to download all your blog posts in bulk (in a zip file having your articles in HTML).</li>
        </ol>
      </section>

      <section className="form-section">
        <h2>Let's Start</h2>
        <div className="form-group">
          <h3>Describe Your Preferences</h3>
          <p>Describe your preferences (e.g., tone, target audience, keywords, length, etc.):</p>
          <textarea value={preferences} onChange={(e) => setPreferences(e.target.value)} rows={5} placeholder="e.g., Friendly tone, SEO keywords: marketing, B2B..." />
        </div>
        <div className="form-group">
          <h3>Enter Your Blog Topics</h3>
          <p>Enter one topic at a time. Press “Add Topic” after each:</p>
          <div className="input-group">
            <input type="text" value={topicInput} onChange={(e) => setTopicInput(e.target.value)} placeholder="e.g., Benefits of remote work" />
            <button className="button-add" onClick={handleAddTopic}>Add Topic</button>
          </div>
          {topics.length > 0 && (
            <ul className="topic-list">
              {topics.map((topic, index) => (
                <li key={index}>
                  <span>{DOMPurify.sanitize(topic)}</span>
                  <button className="button-remove" onClick={() => handleRemoveTopic(index)}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button className="button-submit" onClick={handleSubmit}>Submit</button>
        {showOverlay && (<LoadingOverlay visible={showOverlay} messages={loadingMessages} isSuccess={success} onFadeOut={() => setShowOverlay(false)} />)}
      </section>
    </div>
  );
};

export default BloggenPage;
